import type { ConfigEntry } from '../types';
import type { Cfg } from '../hooks/use-settings-state';

type Entry = ConfigEntry | null | undefined;

function val<T>(entry: Entry, fallback: T): T {
  return (entry?.value as T) ?? fallback;
}

function buildBonuses(ba: Entry): Pick<Cfg, 'bonus_1' | 'bonus_2' | 'bonus_3'> {
  const b = ba?.value as number[] | undefined;
  return {
    bonus_1: b?.[0] ?? 150,
    bonus_2: b?.[1] ?? 100,
    bonus_3: b?.[2] ?? 50,
  };
}

type RaceCfg = Pick<
  Cfg,
  | 'daily_first_to_x_enabled'
  | 'daily_first_to_x_threshold'
  | 'daily_first_to_x_amount_usd'
  | 'weekly_first_to_x_enabled'
  | 'weekly_first_to_x_threshold'
  | 'weekly_first_to_x_amount_usd'
  | 'monthly_first_to_x_enabled'
  | 'monthly_first_to_x_threshold'
  | 'monthly_first_to_x_amount_usd'
>;

function buildRace(r: Entry[]): RaceCfg {
  return {
    daily_first_to_x_enabled: val(r[0], false),
    daily_first_to_x_threshold: val(r[1], 0),
    daily_first_to_x_amount_usd: val(r[2], 0),
    weekly_first_to_x_enabled: val(r[3], false),
    weekly_first_to_x_threshold: val(r[4], 0),
    weekly_first_to_x_amount_usd: val(r[5], 0),
    monthly_first_to_x_enabled: val(r[6], false),
    monthly_first_to_x_threshold: val(r[7], 0),
    monthly_first_to_x_amount_usd: val(r[8], 0),
  };
}

export function buildCfg(d: Entry[], extra: Entry[]): Cfg {
  return {
    payment_cycle_days: val(d[0], 7),
    payment_threshold: val(d[1], 100),
    minimum_leads_count: val(d[2], 100),
    minimum_leads_period: val(d[3], 'weekly'),
    bonus_period: val(d[4], 'weekly'),
    ...buildBonuses(d[5]),
    ranking_period: val(d[6], 'weekly'),
    weekly_goal: val(d[7], 10000),
    total_goal: val(d[8], 100000),
    group_bonus_amount: val(d[9], 33),
    payment_day_of_week: val(extra[0], 1),
    daily_minimum_leads: val(extra[1], 33),
    top_tier_threshold: val(extra[2], 66),
    top_tier_days_required: val(extra[3], 5),
    pre_ban_failed_days_threshold: val(extra[4], 3),
    pre_ban_grace_days: val(extra[5], 2),
    minimum_payout_banned: val(extra[6], 3),
    ...buildRace(extra.slice(7)),
  };
}

function racePayload(cfg: Cfg): Record<string, unknown> {
  return {
    daily_first_to_x_enabled: cfg.daily_first_to_x_enabled,
    daily_first_to_x_threshold: cfg.daily_first_to_x_threshold,
    daily_first_to_x_amount_usd: cfg.daily_first_to_x_amount_usd,
    weekly_first_to_x_enabled: cfg.weekly_first_to_x_enabled,
    weekly_first_to_x_threshold: cfg.weekly_first_to_x_threshold,
    weekly_first_to_x_amount_usd: cfg.weekly_first_to_x_amount_usd,
    monthly_first_to_x_enabled: cfg.monthly_first_to_x_enabled,
    monthly_first_to_x_threshold: cfg.monthly_first_to_x_threshold,
    monthly_first_to_x_amount_usd: cfg.monthly_first_to_x_amount_usd,
  };
}

export function buildPayload(cfg: Cfg): Record<string, unknown> {
  return {
    payment_cycle_days: cfg.payment_cycle_days,
    payment_threshold: cfg.payment_threshold,
    payment_day_of_week: cfg.payment_day_of_week,
    minimum_leads_count: cfg.minimum_leads_count,
    minimum_leads_period: cfg.minimum_leads_period,
    bonus_period: cfg.bonus_period,
    bonus_amounts: [cfg.bonus_1, cfg.bonus_2, cfg.bonus_3],
    ranking_period: cfg.ranking_period,
    weekly_goal: cfg.weekly_goal,
    total_goal: cfg.total_goal,
    group_bonus_amount: cfg.group_bonus_amount,
    daily_minimum_leads: cfg.daily_minimum_leads,
    top_tier_threshold: cfg.top_tier_threshold,
    top_tier_days_required: cfg.top_tier_days_required,
    pre_ban_failed_days_threshold: cfg.pre_ban_failed_days_threshold,
    pre_ban_grace_days: cfg.pre_ban_grace_days,
    minimum_payout_banned: cfg.minimum_payout_banned,
    ...racePayload(cfg),
  };
}
